/**
 * API Throttle Utility
 * Prevence přetížení Mapy.com API při rychlém pohybu v panoramatu
 */

// Stav rate limiteru
let isRateLimited = false;
let rateLimitResetTime: number | null = null;
let consecutiveErrors = 0;
const MAX_CONSECUTIVE_ERRORS = 3;
const BASE_BACKOFF_MS = 1000; // 1 sekunda
const MAX_BACKOFF_MS = 30000; // 30 sekund

// Request queue
const requestQueue: Array<{
    execute: () => Promise<any>;
    resolve: (value: any) => void;
    reject: (reason: any) => void;
}> = [];
let isProcessingQueue = false;

// Minimální delay mezi requesty (ms)
const MIN_REQUEST_DELAY = 200;
let lastRequestTime = 0;

/**
 * Zjistí, zda je API rate limited
 */
export const isApiRateLimited = (): boolean => {
    if (rateLimitResetTime && Date.now() > rateLimitResetTime) {
        isRateLimited = false;
        rateLimitResetTime = null;
    }
    return isRateLimited;
};

/**
 * Nastaví rate limit stav
 */
export const setRateLimited = (durationMs: number = 5000): void => {
    isRateLimited = true;
    rateLimitResetTime = Date.now() + durationMs;
    consecutiveErrors++;
};

/**
 * Resetuje error counter při úspěšném requestu
 */
export const resetErrorCount = (): void => {
    consecutiveErrors = 0;
};

/**
 * Vypočítá backoff delay na základě počtu chyb
 */
export const getBackoffDelay = (): number => {
    const delay = Math.min(
        BASE_BACKOFF_MS * Math.pow(2, consecutiveErrors),
        MAX_BACKOFF_MS
    );
    return delay;
};

/**
 * Zjistí, zda by se měl request přeskočit (circuit breaker)
 */
export const shouldSkipRequest = (): boolean => {
    return consecutiveErrors >= MAX_CONSECUTIVE_ERRORS && isRateLimited;
};

/**
 * Čeká na minimální delay mezi requesty
 */
export const waitForNextSlot = async (): Promise<void> => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;

    if (timeSinceLastRequest < MIN_REQUEST_DELAY) {
        await sleep(MIN_REQUEST_DELAY - timeSinceLastRequest);
    }

    lastRequestTime = Date.now();
};

/**
 * Helper pro sleep
 */
export const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Zpracuje request s throttlingem
 */
export const throttledRequest = async <T>(
    requestFn: () => Promise<T>
): Promise<T> => {
    // Pokud je circuit breaker aktivní, reject
    if (shouldSkipRequest()) {
        throw new Error('API_CIRCUIT_BREAKER_OPEN');
    }

    // Pokud je rate limited, počkej
    if (isApiRateLimited()) {
        const waitTime = rateLimitResetTime ? rateLimitResetTime - Date.now() : getBackoffDelay();
        if (waitTime > 0) {
            await sleep(waitTime);
        }
    }

    // Počkej na další slot
    await waitForNextSlot();

    return requestFn();
};

/**
 * Přidá request do fronty
 */
export const queueRequest = <T>(requestFn: () => Promise<T>): Promise<T> => {
    return new Promise((resolve, reject) => {
        requestQueue.push({
            execute: requestFn,
            resolve,
            reject,
        });

        processQueue();
    });
};

/**
 * Zpracovává frontu requestů
 */
const processQueue = async (): Promise<void> => {
    if (isProcessingQueue || requestQueue.length === 0) {
        return;
    }

    isProcessingQueue = true;

    while (requestQueue.length > 0) {
        const request = requestQueue.shift();
        if (!request) continue;

        try {
            const result = await throttledRequest(request.execute);
            request.resolve(result);
        } catch (err) {
            request.reject(err);
        }
    }

    isProcessingQueue = false;
};

/**
 * Vymaže frontu requestů (např. při změně kola)
 */
export const clearRequestQueue = (): void => {
    while (requestQueue.length > 0) {
        const request = requestQueue.pop();
        if (request) {
            request.reject(new Error('REQUEST_CANCELLED'));
        }
    }
};

/**
 * Resetuje celý stav throttleru
 */
export const resetThrottleState = (): void => {
    isRateLimited = false;
    rateLimitResetTime = null;
    consecutiveErrors = 0;
    clearRequestQueue();
};

/**
 * Detekuje rate limit error z response/error
 */
export const isRateLimitError = (error: any): boolean => {
    if (!error) return false;

    // Error kódy z Mapy.com API
    if (error.errorCode === 'LIMIT_EXCEEDED') return true;

    // HTTP status codes
    if (error.status === 429) return true;
    if (error.status === 504) return true;

    // Error messages
    const message = String(error.message || error).toLowerCase();
    if (message.includes('429')) return true;
    if (message.includes('504')) return true;
    if (message.includes('rate limit')) return true;
    if (message.includes('too many requests')) return true;

    return false;
};

/**
 * Detekuje transient (dočasnou) chybu, která by mohla být vyřešena opakováním
 */
export const isTransientError = (error: any): boolean => {
    if (isRateLimitError(error)) return true;

    if (!error) return false;

    // Network errors
    const message = String(error.message || error).toLowerCase();
    if (message.includes('network')) return true;
    if (message.includes('timeout')) return true;
    if (message.includes('502')) return true;
    if (message.includes('503')) return true;

    return false;
};
